import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Scale } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-emerald-100 hover:text-white hover:bg-emerald-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Scale className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Terms and Conditions</h1>
          </div>
          <p className="text-emerald-100">Last Updated: December 6, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="prose prose-sm max-w-none pt-6">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using LexProcure PH ("Service"), you agree to be bound by these Terms and Conditions. 
                If you do not agree with any part of these terms, you may not access the Service.
              </p>
              <p className="text-gray-700">
                LexProcure PH is a proprietary application owned by <strong>SRAGCircle OPC</strong>, developed under its portfolio brand <strong>LexDoRight</strong> ("we," "us," or "our"). The Service provides access to 
                Philippine government procurement laws, regulations, and related legal resources.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. User Accounts</h2>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.1 Registration</h3>
              <p className="text-gray-700 mb-4">
                To access certain features of the Service, you must register for an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.2 Trial Period</h3>
              <p className="text-gray-700 mb-4">
                New users receive a 10-day free trial period from registration. During this period, you have full 
                access to all Service features. After the trial expires, access will be restricted until you purchase a subscription.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.3 Session Limit</h3>
              <p className="text-gray-700 mb-4">
                Each account is limited to <strong>2 concurrent sessions</strong>. This means you can be logged 
                in on a maximum of 2 sessions (computers, tablets, phones) at the same time. If you attempt to log in 
                on a third device while already logged in on 2 other devices, you will be required to log out from 
                one of the existing devices first.
              </p>
              <p className="text-gray-700 mb-4">
                Sessions are automatically managed and will expire after 24 hours of inactivity. Sharing your 
                account credentials with others to circumvent this limit is a violation of these Terms.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.4 Account Termination</h3>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your account at any time for violations of these Terms, 
                fraudulent activity, or any other reason we deem necessary to protect the Service and its users.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Subscription and Payment</h2>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.1 Pricing</h3>
              <p className="text-gray-700 mb-4">
                Current subscription pricing:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>First Year: ₱699.00 + VAT</li>
                <li>Succeeding Year: ₱99.00 + VAT</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Prices are subject to change with 30 days' notice to existing subscribers. Price changes will not affect current subscription periods.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.2 Payment Methods</h3>
              <p className="text-gray-700 mb-4">
                We accept payments through GCash, Maya, bank transfer, and other payment methods as made available. 
                All payments are processed securely.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.3 Automatic Renewal</h3>
              <p className="text-gray-700 mb-4">
                Subscriptions do not automatically renew. You will receive email notifications before your subscription 
                expires, and you must manually renew to continue service.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">3.4 Refunds and Access Termination</h3>
              <p className="text-gray-700 mb-4">
                Please refer to our Refund Policy for detailed information about refunds and cancellations.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> By requesting and accepting a refund, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your access to the Service will be immediately terminated upon refund approval</li>
                <li>You will no longer be authorized to use the application, access content, or utilize any features</li>
                <li>Any attempt to access or use the Service after receiving a refund constitutes unauthorized use</li>
                <li>Unauthorized use or access is a violation of these Terms and may result in legal action</li>
                <li>You must cease all use of any materials, content, or data obtained from the Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">You agree NOT to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Share your account credentials with others or exceed the 2-device concurrent use limit</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Access or use the Service after your subscription has expired or been terminated</li>
                <li>Continue to use the Service, its content, or materials after receiving a refund</li>
                <li>Scrape, copy, or download content for redistribution</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated tools (bots, scrapers) to access the Service</li>
                <li>Resell, redistribute, or sublicense access to the Service</li>
                <li>Remove or modify any copyright or proprietary notices</li>
                <li>Attempt to circumvent session limits or authentication mechanisms</li>
              </ul>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
                <p className="text-red-900 font-semibold mb-2">⚠️ Unauthorized Use After Refund</p>
                <p className="text-red-800 text-sm">
                  Any attempt to access, use, or retain content from the Service after receiving a refund is strictly 
                  prohibited and constitutes a material breach of these Terms. We reserve the right to pursue all available 
                  legal remedies, including but not limited to civil and criminal prosecution, for unauthorized access or use.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The content, organization, graphics, design, and other matters related to the Service are protected 
                under applicable copyrights and other proprietary laws. The compilation, aggregation, and organization 
                of legal content is our proprietary work.
              </p>
              <p className="text-gray-700 mb-4">
                Philippine laws and regulations (RA 12009, RA 9184, IRRs, etc.) are public domain documents. Our 
                value-added features including search, organization, AI analysis, and tools are proprietary.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Disclaimer of Warranties</h2>
              <p className="text-gray-700 mb-4">
                <strong>IMPORTANT LEGAL NOTICE:</strong>
              </p>
              <p className="text-gray-700 mb-4">
                The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind. We do not warrant that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>The Service will be uninterrupted or error-free</li>
                <li>The legal information is complete, accurate, or current</li>
                <li>Any defects will be corrected</li>
                <li>The Service is free of viruses or harmful components</li>
              </ul>
              <p className="text-gray-700 mb-4 font-semibold">
                LexProcure PH is a research and reference tool ONLY. It does not provide legal advice. 
                Always consult with a qualified attorney for legal matters.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, SRAGCircle OPC, LexDoRight, and its operators shall not be liable for any 
                indirect, incidental, special, consequential, or punitive damages resulting from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your use or inability to use the Service</li>
                <li>Any reliance on information obtained through the Service</li>
                <li>Errors or omissions in the content</li>
                <li>Unauthorized access to your account</li>
                <li>Any other matter relating to the Service</li>
              </ul>
              <p className="text-gray-700">
                Our total liability shall not exceed the amount paid by you for the Service in the past 12 months.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless SRAGCircle OPC, LexDoRight, its officers, directors, employees, and agents 
                from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your 
                use of the Service or violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of material changes via 
                email or through the Service. Continued use of the Service after changes constitutes acceptance of the 
                new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the Republic of the Philippines, 
                without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700">
                Any disputes arising from these Terms or use of the Service shall be subject to the exclusive jurisdiction 
                of the courts of the Philippines.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Company:</strong> SRAGCircle OPC (via LexDoRight)</p>
                <p className="text-gray-700"><strong>Email:</strong> admin@lexdoright.com</p>
                <p className="text-gray-700"><strong>Website:</strong> lexdoright.com</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited 
                or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <div className="mt-8 p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-800">
                By using LexProcure PH, you acknowledge that you have read, understood, and agree to be bound by these 
                Terms and Conditions.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
